// @greenlight-spec: 19-design-system.md
import * as React from 'react';

import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';

export interface ToggleRowProps {
  label: string;
  helper?: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  id?: string;
}

export function ToggleRow({
  label,
  helper,
  checked,
  onCheckedChange,
  disabled = false,
  id,
}: ToggleRowProps): React.ReactElement {
  // useId() provides a stable unique ID when the caller does not supply one.
  const generatedId = React.useId();
  const switchId = id ?? generatedId;
  const labelId = `${switchId}-label`;

  return (
    <div className="flex items-center justify-between gap-4 px-5 py-3.5">
      {/* Label + optional helper — associated with the switch via aria-labelledby */}
      <div className="flex min-w-0 flex-col gap-0.5">
        <label
          id={labelId}
          htmlFor={switchId}
          className={cn(
            'cursor-pointer select-none text-sm text-foreground',
            disabled && 'cursor-not-allowed opacity-50',
          )}
        >
          {label}
        </label>
        {helper && <p className="text-xs text-muted-foreground">{helper}</p>}
      </div>

      {/* The switch is the interactive control; h-11 touch target on mobile via the row */}
      <Switch
        id={switchId}
        aria-labelledby={labelId}
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        className="shrink-0"
      />
    </div>
  );
}
